import { Alert, Box, CircularProgress, Divider, Grid, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import AdultOccupant from '../../../../../icons/AdultOccupant.svg';
import ApplicationPet from '../../../../../icons/Pet.svg';
import ApplicationDollar from '../../../../../icons/ApplicationDollar.svg';
import ApplicationVehicle from '../../../../../icons/Vehicle.svg';
import ApplicationHouse from '../../../../../icons/MoveInDateHouse.svg';
import ApplicationChildren from '../../../../../icons/childrens.svg';
import ApplicationDetailsSummaryCoverSheetItemTile, { ApplicationDetailsSummaryCoverSheetItemType } from './ApplicationDetailsSummaryCoverSheetItemTile';
import ScreeningSpyIcon from '../../../../../icons/screening_spy1.svg';
import ReviewOtpIcon from '../../../../../icons/ReviewOtpIcon.svg';
import { useHistory, useLocation } from 'react-router';
import {
  ApplicantTypeEnum,
  ApplicationFileTypeEnum,
  ApplicationOccupantSummaryDto,
  ApplicationOccupantSummaryDtoStatusEnum,
  ApplicationStatus,
  ApplicationSummaryDto,
  ApplicationV2Dto,
  ScreeningOrderDto,
} from '@inosio/payments-sdk';
import DownloadBlack from '../../../../../icons/DownloadBlack.svg';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportApplicationZip,
  exportEmploymentRelease,
  exportRentalRelease,
  fetchEmploymentReleaseURL,
  fetchRentalHistoryReleaseURL,
  getExportApplicationDetails,
  getExportApplicationPdf,
  selectApplicationsState,
} from '../../../../../resui/applications/sagas/pm/applicationsSlice';
import ApplicationDetailsSummaryDownloadsRow from './ApplicationDetailsSummaryDownloadsRow';
import ApplicationDetailsNotes from './ApplicationDetailsNotes';
import OrderScreeningOptionsModal from '../../OrderScreeningOptionsModal';
import { formatE164PhoneNumber } from '../../../../../shared/util/formaters';
import { OrderScreeningListModal } from '../../OrderScreeningListModal';
import ApplicantNotification from '../../../../../icons/ApplicantNotification.svg';
import ApplicantNotificationModal from '../../ApplicantNotificationModal';
import DecisionApplicationModal, { DecisionModalStates } from '../../DecisionApplicationModal';

export interface ApplicationDetailsSummaryProps {
  application: ApplicationV2Dto;
}
export default function ApplicationDetailsSummary(props: ApplicationDetailsSummaryProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { state: appDetailsSummaryState } = useLocation<{ returnQueryString: string }>();
  const [openOrderScreeningOptionsModal, setOpenOrderScreeningOptionsModal] = useState(false);
  const [openOrderScreeningListModal, setOpenScreeningModalList] = useState<boolean>(false);
  const [screeningModalApplicantName, setScreeningModalApplicantName] = useState<string>();
  const [applicantScreeningOrders, setApplicantScreeningOrders] = useState<ScreeningOrderDto[]>([]);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [localError, setLocalError] = useState('');
  const [openApplicantNotificationModal, setOpenApplicantNotificationModal] = useState(false);
  const [openAppDecisionModal, setOpenAppDecisionModal] = useState(false);

  const {
    isFetchingEmploymentReleaseURLSuccess,
    isFetchingRentalHistoryReleaseURLSuccess,
    isFetchingRentalHistoryReleaseURLFailed,
    isFetchingEmploymentReleaseURLFailed,
    isFetchingRentalHistoryReleaseURL,
    isFetchingEmploymentReleaseURL,
    isExportingApplicationDetails,
    isExportingEmploymentRelease,
    isExportingRentalRelease,
    isExportingEmploymentReleaseSuccess,
    isExportingEmploymentReleaseFailed,
    isExportingRentalReleaseSuccess,
    isExportingRentalReleaseFailed,
    isExportApplicationDetailsFailed,
    isExportApplicationDetailsSuccess,
    isFetchingApplicationPdfURL,
    isFetchingApplicationPdfURLSuccess,
    isFetchingApplicationPdfURLFailed,
    allApplicationNotes,
    isExportingApplicationZip,
    isExportingApplicationZipFailure,
    isExportingApplicationZipSuccess,
  } = useSelector(selectApplicationsState);

  const allApplicantsOnThisApplicationRegardlessOfType = [...(props.application.CoApplicants || []), props.application.PrimaryApplicant];
  const allApplicantsOnThisApplicationThatAreOccupants = allApplicantsOnThisApplicationRegardlessOfType.filter((app) => app.ApplicantType === ApplicantTypeEnum.Occupant);
  const allApplicantsOnThisApplicationThatAreCosigners = allApplicantsOnThisApplicationRegardlessOfType.filter((app) => app.ApplicantType === ApplicantTypeEnum.Cosigner);

  const appSummaryObj: ApplicationSummaryDto = {
    Uuid: props.application.Uuid,
    Status: props.application.Status,
    PropertyAddress: props.application.PropertyAddress,
    OrganizationUuid: props.application.OrganizationUuid,
    OrganizationName: props.application.OrganizationName,
    CreatedDate: props.application.CreatedDate,
    PrimaryApplicantUuid: props.application.PrimaryApplicant.Uuid,
    Applicants: allApplicantsOnThisApplicationRegardlessOfType.map((applicant) => {
      return {
        Uuid: applicant.Uuid,
        Email: applicant.Email,
        UserRegistrationDate: applicant.UserRegistrationDate,
        LastName: applicant.LastName,
        MiddleName: applicant.MiddleName,
        FirstName: applicant.FirstName,
        Status: applicant.Status as unknown as ApplicationOccupantSummaryDtoStatusEnum,
        ApplicantType: applicant.ApplicantType,
      } as ApplicationOccupantSummaryDto;
    }),
    ScreeningOrders: props.application.ScreeningOrders,
  };

  // Calculate the combined monthly income, total occupant count, total adult applicant count, total vehicle count and total animal count of all applicants
  const { totalMonthlyIncome, totalMinorOccupantCount, totalAdultApplicantCount, totalAnimalCount, totalVehicleCount } =
    allApplicantsOnThisApplicationThatAreOccupants &&
    allApplicantsOnThisApplicationThatAreOccupants.reduce(
      (totals, applicant) => {
        // Calculate the total Minor occupant count for this applicant
        const totalMinorOccupantCountForThisApplicant = applicant.Minors?.length || 0;

        // Calculate the total animal count for this applicant
        const totalAnimalCountForThisApplicant = applicant.Pets?.length || 0;

        // Calculate the total Vehicle count for this applicant
        const totalVehicleCountForThisApplicant = applicant.Vehicles?.length || 0;

        // Filter employments for the current applicant based on the presence of an EndDate
        const currentEmployments = applicant.EmploymentHistories.filter((emp) => emp.EndDate === null || emp.EndDate === '');

        // Calculate the total income from all employments for the current applicant
        const allEmploymentsIncome = currentEmployments.reduce((sum, emp) => sum + (emp.MonthlySalary || 0) + (emp.OtherIncomeAmount || 0), 0); //income amounts are nullable in the db

        // Update the total monthly income, total occupant count, total adult applicant count, and total animal count
        totals.totalMonthlyIncome += allEmploymentsIncome;
        totals.totalMinorOccupantCount += totalMinorOccupantCountForThisApplicant;
        totals.totalAdultApplicantCount += 1; // Increment the applicant count by 1
        totals.totalAnimalCount += totalAnimalCountForThisApplicant; // Add the animal count for this applicant
        totals.totalVehicleCount += totalVehicleCountForThisApplicant; // Add the vehicle count for this applicant

        // Return updated totals
        return totals;
      },
      { totalMonthlyIncome: 0, totalMinorOccupantCount: 0, totalAdultApplicantCount: 0, totalAnimalCount: 0, totalVehicleCount: 0 },
    );

  //error handling for viewing the docs
  useEffect(() => {
    if (isFetchingEmploymentReleaseURLSuccess && isFetchingRentalHistoryReleaseURLSuccess && isFetchingApplicationPdfURLSuccess) {
      setLocalError('');
      setSnackBarOpen(false);
    } else if (isFetchingEmploymentReleaseURLFailed || isFetchingRentalHistoryReleaseURLFailed || isFetchingApplicationPdfURLFailed) {
      setSnackBarOpen(true);
      setLocalError(`Apologies, the request to view the document failed. Please try again later or contact support. Thank you.`);
    }
  }, [
    isFetchingEmploymentReleaseURLSuccess,
    isFetchingRentalHistoryReleaseURLSuccess,
    isFetchingApplicationPdfURLSuccess,
    isFetchingEmploymentReleaseURLFailed,
    isFetchingRentalHistoryReleaseURLFailed,
    isFetchingApplicationPdfURLFailed,
  ]);

  //error handling for downloading the docs
  useEffect(() => {
    if (isExportingEmploymentReleaseSuccess && isExportingRentalReleaseSuccess && isExportApplicationDetailsSuccess && isExportingApplicationZipSuccess) {
      setLocalError('');
      setSnackBarOpen(false);
    } else if (isExportingEmploymentReleaseFailed || isExportingRentalReleaseFailed || isExportApplicationDetailsFailed || isExportingApplicationZipFailure) {
      setSnackBarOpen(true);
      setLocalError(`Apologies, the request to download the document failed. Please try again later or contact support. Thank you.`);
    }
  }, [
    isExportingEmploymentReleaseSuccess,
    isExportingRentalReleaseSuccess,
    isExportApplicationDetailsSuccess,
    isExportingEmploymentReleaseFailed,
    isExportingRentalReleaseFailed,
    isExportApplicationDetailsFailed,
    isExportingApplicationZipFailure,
    isExportingApplicationZipSuccess,
  ]);

  const handleDownloadAllDocs = () => {
    dispatch({
      type: exportApplicationZip.type,
      payload: {
        AppUuid: props.application.Uuid,
        DesiredFileDto: { Type: ApplicationFileTypeEnum.ApplicationDataZip },
      },
    });
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {localError}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          width: '75%',
          overflow: 'auto',
          padding: 2,
          height: 'calc( 100vh - 294px )',
          '&::-webkit-scrollbar': {
            width: 7,
            height: 7,
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
            borderRadius: '24px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'darkgrey',
            borderRadius: '24px',
          },
        }}
      >
        {/** Summary Icon Tiles */}
        <Grid container spacing={2} mb={4}>
          {/* Row 1 */}
          <Grid item xs={12} sm={4}>
            <ApplicationDetailsSummaryCoverSheetItemTile
              itemType={ApplicationDetailsSummaryCoverSheetItemType.TotalAdults}
              itemIconUrl={AdultOccupant}
              itemTitle="Adults"
              itemSubtitle={
                allApplicantsOnThisApplicationThatAreCosigners.length > 0
                  ? `+ ${allApplicantsOnThisApplicationThatAreCosigners.length} ${allApplicantsOnThisApplicationThatAreCosigners.length > 1 ? 'Cosigners' : 'Cosigner'}`
                  : undefined
              }
              itemValue={`x ${totalAdultApplicantCount}`}
              itemIconHeight="40px"
              itemIconWidth="20px"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ApplicationDetailsSummaryCoverSheetItemTile
              itemType={ApplicationDetailsSummaryCoverSheetItemType.TotalPets}
              itemIconUrl={ApplicationPet}
              itemTitle="Animals"
              itemValue={`x ${totalAnimalCount}`}
              itemIconHeight="35px"
              itemIconWidth="35px"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ApplicationDetailsSummaryCoverSheetItemTile
              itemType={ApplicationDetailsSummaryCoverSheetItemType.TotalIncome}
              itemIconUrl={ApplicationDollar}
              itemTitle="Income"
              itemValue={totalMonthlyIncome}
              itemIconHeight="30px"
              itemIconWidth="30px"
            />
          </Grid>
          {/* Row 2 */}
          <Grid item xs={12} sm={4}>
            <ApplicationDetailsSummaryCoverSheetItemTile
              itemType={ApplicationDetailsSummaryCoverSheetItemType.TotalChildren}
              itemIconUrl={ApplicationChildren}
              itemTitle="Children"
              itemValue={`x ${totalMinorOccupantCount}`}
              itemIconHeight="40px"
              itemIconWidth="40px"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ApplicationDetailsSummaryCoverSheetItemTile
              itemType={ApplicationDetailsSummaryCoverSheetItemType.TotalVehicles}
              itemIconUrl={ApplicationVehicle}
              itemTitle="Vehicles"
              itemValue={`x ${totalVehicleCount}`}
              itemIconHeight="50px"
              itemIconWidth="50px"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ApplicationDetailsSummaryCoverSheetItemTile
              itemType={ApplicationDetailsSummaryCoverSheetItemType.MoveInDate}
              itemIconUrl={ApplicationHouse}
              itemTitle="Move In"
              itemValue={(() => {
                // Initialize the variable for the formatted date
                let desiredMoveInDate = '';
                // Check if DesiredMoveInDate exists and format it
                if (props.application.DesiredMoveInDate) {
                  const parsedDate = parseISO(props.application.DesiredMoveInDate);
                  const isValidDate = isValid(parsedDate);
                  if (isValidDate) {
                    desiredMoveInDate = format(new Date(props.application.DesiredMoveInDate + 'T00:00:00'), 'MM/dd/yyyy');
                  }
                }
                // Return the formatted date or an empty string if not valid
                return desiredMoveInDate || '';
              })()}
              itemIconHeight="50px"
              itemIconWidth="50px"
            />
          </Grid>
        </Grid>
        {/** Summary Applicant Details */}
        <Box sx={{ width: '100%' }}>
          <TableContainer
            sx={{
              flex: 1,
              scrollbarWidth: 'thin',
            }}
          >
            <Table size="small" aria-label="purchases">
              <TableHead>
                <TableRow>
                  <TableCell id="subTitle">Name</TableCell>
                  <TableCell id="subTitle">Email</TableCell>
                  <TableCell id="subTitle">Phone</TableCell>
                  <TableCell id="subTitle" align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allApplicantsOnThisApplicationRegardlessOfType?.length &&
                  allApplicantsOnThisApplicationRegardlessOfType?.map((app, idx) => {
                    const screeningOrderUuid = props.application.ScreeningOrders?.find((so) => so.ApplicantUuid === app.Uuid)?.ScreeningOrderUuid;
                    const applicantScreeningOrders = props.application.ScreeningOrders.filter((so) => so.ApplicantUuid === app.Uuid);
                    return (
                      <TableRow
                        key={app.Uuid}
                        sx={{
                          '& .MuiTableCell-root': {
                            borderBottom: allApplicantsOnThisApplicationRegardlessOfType.length === idx + 1 ? 'none' : '',
                          },
                        }}
                      >
                        <TableCell id="content">
                          {(app?.FirstName || '') + ' ' + (app?.LastName || '')}
                          <Typography
                            sx={{
                              fontFamily: 'Work Sans !important',
                              fontStyle: 'normal !important',
                              fontWeight: '400 !important',
                              fontSize: '14px !important',
                              color: '#A8A8A8 !important',
                            }}
                          >
                            {app.ApplicantType}
                          </Typography>
                        </TableCell>
                        <TableCell id="content">{app.Email || ''}</TableCell>
                        <TableCell id="content">{formatE164PhoneNumber(app.PhoneNumber || '')}</TableCell>
                        <TableCell id="content">
                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            <Tooltip title="Order Screening" placement="top" onClick={(e) => {}}>
                              <Box display="flex" justifyContent="center" alignItems="center" width={34} height={34}>
                                <img
                                  src={ScreeningSpyIcon}
                                  alt="Order Screening"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    setOpenOrderScreeningOptionsModal(true);
                                  }}
                                />
                              </Box>
                            </Tooltip>
                            {screeningOrderUuid && (
                              <Tooltip title="View Screening" placement="top" onClick={(e) => {}}>
                                <Box display="flex" justifyContent="center" alignItems="center" width={34} height={34}>
                                  <img
                                    width={13}
                                    height={14}
                                    src={ReviewOtpIcon}
                                    alt="details"
                                    style={{ cursor: 'pointer', visibility: props.application.ScreeningOrders?.length > 0 ? 'visible' : 'hidden' }}
                                    onClick={() => {
                                      if (applicantScreeningOrders.length > 1) {
                                        setApplicantScreeningOrders(applicantScreeningOrders);
                                        setScreeningModalApplicantName([app?.FirstName, app?.MiddleName, app?.LastName].filter(Boolean).join(' '));
                                        setOpenScreeningModalList(true);
                                      } else {
                                        history.push(`/pmui/applications/${props.application.Uuid}/screening/${screeningOrderUuid}`, {
                                          returnQueryString: appDetailsSummaryState?.returnQueryString || '',
                                        });
                                      }
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            )}
                            {[ApplicationStatus.Rejected, ApplicationStatus.Approved].includes(props.application.Status as ApplicationStatus) && (
                              <Tooltip
                                title="Notify applicant about the decision"
                                placement="top"
                                onClick={() => {
                                  if (props.application.Status === ApplicationStatus.Rejected) {
                                    setOpenApplicantNotificationModal(true);
                                  } else if (props.application.Status === ApplicationStatus.Approved) {
                                    setOpenAppDecisionModal(true);
                                  }
                                }}
                              >
                                <Box display="flex" justifyContent="center" alignItems="center" width={34} height={34}>
                                  <img width={16} height={17} src={ApplicantNotification} alt="details" style={{ cursor: 'pointer' }} onClick={() => {}} />
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {/** Summary Downloads Section */}
        <Box sx={{ width: '100%', marginTop: 5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 1, paddingLeft: 2 }}>
            <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: 'large', fontWeight: 'bold' }}>Downloads</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ cursor: 'pointer' }} onClick={handleDownloadAllDocs}>
                <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: 'large' }}>Export Application Data</Typography>
              </Box>
              {isExportingApplicationZip ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={18} style={{ marginLeft: 20 }} />
                </Box>
              ) : (
                <img src={DownloadBlack} alt="download all" width={13} height={14} style={{ marginLeft: 20, cursor: 'pointer' }} onClick={handleDownloadAllDocs} />
              )}
            </Box>
          </Box>
          <Divider />
          <ApplicationDetailsSummaryDownloadsRow
            isFetchingDocumentURL={isFetchingApplicationPdfURL}
            isDownloadingDocumentURL={isExportingApplicationDetails}
            title="Application PDF"
            handleClickDownload={() => {
              dispatch({
                type: getExportApplicationDetails.type, //downloads the app pdf
                payload: {
                  AppUuid: props.application?.Uuid ?? '',
                  DesiredFileDto: { Type: ApplicationFileTypeEnum.ApplicationDetailsPdf },
                },
              });
            }}
            handleClickView={() => {
              dispatch({
                type: getExportApplicationPdf.type, //downloads the app pdf url
                payload: {
                  AppUuid: props.application.Uuid,
                },
              });
            }}
          />
          <Divider />
          <ApplicationDetailsSummaryDownloadsRow
            isFetchingDocumentURL={isFetchingEmploymentReleaseURL}
            isDownloadingDocumentURL={isExportingEmploymentRelease}
            title="Employment Verification Release"
            handleClickDownload={() => {
              allApplicantsOnThisApplicationRegardlessOfType.forEach((app) => {
                dispatch({
                  type: exportEmploymentRelease.type,
                  payload: {
                    AppUuid: props.application?.Uuid ?? '',
                    DesiredFileDto: { Type: ApplicationFileTypeEnum.EmploymentHistoryRelease, ApplicantUuid: app.Uuid },
                  },
                });
              });
            }}
            handleClickView={() => {
              allApplicantsOnThisApplicationRegardlessOfType.forEach((app) => {
                dispatch({
                  type: fetchEmploymentReleaseURL.type,
                  payload: {
                    AppUuid: props.application?.Uuid ?? '',
                    DesiredFileDto: { Type: ApplicationFileTypeEnum.EmploymentHistoryRelease, ApplicantUuid: app.Uuid },
                  },
                });
              });
            }}
          />
          <Divider />
          <ApplicationDetailsSummaryDownloadsRow
            isFetchingDocumentURL={isFetchingRentalHistoryReleaseURL}
            isDownloadingDocumentURL={isExportingRentalRelease}
            title="Rental History Verification Release"
            handleClickDownload={() => {
              allApplicantsOnThisApplicationRegardlessOfType.forEach((app) => {
                dispatch({
                  type: exportRentalRelease.type,
                  payload: {
                    AppUuid: props.application?.Uuid ?? '',
                    DesiredFileDto: { Type: ApplicationFileTypeEnum.RentalHistoryRelease, ApplicantUuid: app.Uuid },
                  },
                });
              });
            }}
            handleClickView={() => {
              allApplicantsOnThisApplicationRegardlessOfType.forEach((app) => {
                dispatch({
                  type: fetchRentalHistoryReleaseURL.type,
                  payload: {
                    AppUuid: props.application?.Uuid ?? '',
                    DesiredFileDto: { Type: ApplicationFileTypeEnum.RentalHistoryRelease, ApplicantUuid: app.Uuid },
                  },
                });
              });
            }}
          />
        </Box>
      </Box>
      <ApplicationDetailsNotes notes={allApplicationNotes || []} appUuid={props.application.Uuid} />
      {openOrderScreeningOptionsModal && (
        <OrderScreeningOptionsModal
          open={openOrderScreeningOptionsModal}
          onClose={() => {
            setOpenOrderScreeningOptionsModal(false);
          }}
          application={appSummaryObj}
        />
      )}
      {openOrderScreeningListModal && (
        <OrderScreeningListModal
          applicantScreeningOrders={applicantScreeningOrders}
          applicantName={screeningModalApplicantName}
          appDetailsSummaryState={appDetailsSummaryState}
          open={openOrderScreeningListModal}
          onClose={() => {
            setOpenScreeningModalList(false);
          }}
          applicationUuid={appSummaryObj.Uuid}
        />
      )}
      <ApplicantNotificationModal
        open={openApplicantNotificationModal}
        onClose={() => {
          setOpenApplicantNotificationModal(false);
        }}
        application={appSummaryObj}
        applicationDecision={
          props.application?.Status === ApplicationStatus.Rejected ? ApplicationStatus.Rejected : props.application?.Status === ApplicationStatus.Approved ? ApplicationStatus.Approved : null
        }
      />
      <DecisionApplicationModal
        open={openAppDecisionModal}
        propertyGroupUuid={props.application?.OrganizationUuid || ''}
        onClose={() => setOpenAppDecisionModal(false)}
        application={appSummaryObj}
        initialModalState={DecisionModalStates.AcceptanceTerms}
        acceptanceTerms={props.application.AcceptanceTerms?.Terms}
      />
    </Box>
  );
}
